"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDeleteFormProcessData } from "@/hooks/form/use-form";
import { useGetProcessesByFormId } from "@/hooks/process/use-process";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface DeleteDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
}

const DeleteDataModal: React.FC<DeleteDataModalProps> = ({
  isOpen,
  onClose,
  formId,
}) => {
  const { t } = useTranslation();
  const [selectedProcessId, setSelectedProcessId] = useState<string>("");

  const { data: processes, isLoading: isLoadingProcesses } =
    useGetProcessesByFormId(formId);
  const deleteMutation = useDeleteFormProcessData();

  const handleDelete = async () => {
    if (!selectedProcessId) {
      return;
    }

    await deleteMutation.mutateAsync({
      processId: selectedProcessId,
      formId,
    });

    onClose();
  };

  const handleClose = () => {
    setSelectedProcessId("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('common.delete')} {t('common.downloadData')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('common.warning')}: This action will permanently delete all applicant data for the selected process. This cannot be undone.
          </p>

          {isLoadingProcesses ? (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">{t('common.loading')}</p>
            </div>
          ) : processes?.data && processes?.data?.length > 0 ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {t('processManagement.selectProcessName')}
              </label>
              <Select value={selectedProcessId} onValueChange={setSelectedProcessId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('processManagement.selectProcessName')} />
                </SelectTrigger>
                <SelectContent>
                  {processes?.data?.map((process: any) => (
                    <SelectItem key={process.id} value={process.id}>
                      {process.processName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-red-500">
                {t('processManagement.noProcessFound')}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDelete}
              disabled={!selectedProcessId || deleteMutation.isPending}
              variant="destructive"
            >
              {deleteMutation.isPending ? t('common.loading') : t('common.delete')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteDataModal;
